/**
 * API Client for ZITADEL Backend Integration
 *
 * This client handles all HTTP communication with the ZITADEL backend,
 * including automatic token management, request/response interceptors,
 * and error handling.
 */

import { tokenStore } from '@peritasai/data-access';
import { logger } from '@peritasai/support-logging';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { env } from '../../../shared/utils/env';

/**
 * Backend API Response Format
 */
export interface BackendApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Backend User Format (from ZITADEL)
 * roles and permissions optional - will be removed from callback, use JWT groups instead
 */
export interface BackendUser {
  userId?: string; // legacy
  id?: string; // callback payload uses id
  email: string;
  firstName: string;
  lastName: string;
  roles?: string[];
  permissions?: string[];
  isActive?: boolean;
  lastActivity?: number;
}

/**
 * Token Exchange Request
 */
export interface TokenExchangeRequest {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

/**
 * Token data from callback/refresh response
 */
export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
}

/**
 * Token Exchange Response (JWT-based)
 * Backend returns tokens for frontend to store and send as Bearer
 */
export interface TokenExchangeResponse {
  success: boolean;
  user: BackendUser;
  sessionId?: string; // legacy, deprecated
  tokenResponse: TokenResponse;
}

/**
 * Refresh Token Request
 */
export interface RefreshTokenRequest {
  refresh_token: string;
}

/**
 * Logout Response
 */
export interface LogoutResponse {
  success: boolean;
  message: string;
  logoutUrl?: string | null;
}

/**
 * API Client Configuration
 */
interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  withCredentials: boolean;
}

/**
 * ZITADEL Backend API Client
 *
 * Handles all communication with the ZITADEL backend including:
 * - Token exchange for PKCE flow
 * - User session management
 * - Error handling
 */

export function handleApiResponseError(error: AxiosError): Promise<never> {
  if (axios.isAxiosError(error) && error.response) {
    const status = error.response.status;
    logger?.logError({
      eventName: 'fe.api_error',
      severity: status >= 500 ? 'error' : 'warn',
      serviceName: 'periverse_app',
      description: error.message,
      extra: { status },
    });
  }
  return Promise.reject(error);
}

export class ApiClient {
  private client: AxiosInstance;
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
    this.client = this.createAxiosInstance();
    this.setupInterceptors();
  }

  /**
   * Create axios instance with base configuration
   */
  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      withCredentials: this.config.withCredentials,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - add Bearer token for session/me and logout
    // Skip for callback and refresh (unauthenticated)
    // Uses getAccessTokenOrRefresh(): restores from sessionStorage on refresh, proactively refreshes if expired
    this.client.interceptors.request.use(
      async (config) => {
        const isUnauthenticatedEndpoint =
          config.url?.includes('/auth/pkce/callback') ||
          config.url?.includes('/auth/pkce/refresh');
        if (!isUnauthenticatedEndpoint) {
          const token = await tokenStore.getAccessTokenOrRefresh();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      handleApiResponseError
    );
  }

  /**
   * Exchange authorization code for tokens
   */
  async exchangeTokens(
    request: TokenExchangeRequest
  ): Promise<TokenExchangeResponse> {
    try {
      const response = await this.client.post<TokenExchangeResponse>(
        '/auth/pkce/callback',
        request
      );

      if (!response.data.success) {
        throw new Error('Token exchange failed');
      }

      return response.data;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshTokens(refreshToken: string): Promise<TokenResponse> {
    try {
      const response = await this.client.post<{
        success?: boolean;
        access_token: string;
        refresh_token: string;
        expires_in: number;
        token_type?: string;
        id_token?: string;
      }>('/auth/pkce/refresh', { refresh_token: refreshToken });

      const data = response.data;
      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
        token_type: data.token_type ?? 'Bearer',
        id_token: data.id_token,
      };
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Logout user.
   * Bearer token in Authorization header is sufficient; no body required.
   */
  async logout(): Promise<LogoutResponse> {
    try {
      const response = await this.client.post<LogoutResponse>('/auth/logout');

      if (!response.data.success) {
        throw new Error('Logout failed');
      }

      return response.data;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Handle API errors
   */
  private handleApiError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const responseData = error.response?.data as
        | { error?: string }
        | undefined;
      const message = responseData?.error || error.message;

      switch (status) {
        case 400:
          return new Error(`Bad Request: ${message}`);
        case 401:
          return new Error(`Unauthorized: ${message}`);
        case 403:
          return new Error(`Forbidden: ${message}`);
        case 404:
          return new Error(`Not Found: ${message}`);
        case 500:
          return new Error(`Server Error: ${message}`);
        default:
          return new Error(`API Error: ${message}`);
      }
    }

    const message = error instanceof Error ? error.message : 'Unknown error';
    return new Error(`Unknown Error: ${message}`);
  }

  /**
   * Get the underlying axios instance
   */
  getClient(): AxiosInstance {
    return this.client;
  }

  /**
   * Update base URL
   */
  updateBaseURL(baseURL: string): void {
    this.config.baseURL = baseURL;
    this.client.defaults.baseURL = baseURL;
  }
}

/**
 * Create API client instance
 * Uses environment variables for the backend URL
 */
export const createApiClient = (): ApiClient => {
  const baseURL = env.PERIVERSE_AUTH_API_URL;

  return new ApiClient({
    baseURL,
    timeout: 10000, // 10 seconds
    withCredentials: false, // Bearer token used instead of cookies
  });
};

// Export singleton instance
export const apiClient = createApiClient();
